/**
 * Shared input validators for forms where data quality matters
 * (adoption applications, guardian family commitments).
 *
 * These intentionally do NOT run on donation forms — donations
 * optimize for "convert now, validate later".
 */

// Turkish phone numbers: 10-11 digits, starting with 0 (or no leading 0).
// Accepts only digits/spaces/+/-/() during typing, validates the final form.
export function isValidTurkishPhone(raw) {
  if (!raw) return false;
  const digits = String(raw).replace(/\D/g, "");
  // Strip leading 0 or 90 country code.
  let body = digits;
  if (body.startsWith("90") && body.length === 12) body = body.slice(2);
  if (body.startsWith("0")) body = body.slice(1);
  // GSM and landline both = 10 digits after normalisation.
  return body.length === 10 && /^[1-9]/.test(body);
}

// Standard RFC-compliant-enough email regex (covers 99% of valid addresses).
// Rejects double TLDs like ".commmmmm" via length cap on each label.
const EMAIL_RE =
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,24}$/;

export function isValidEmail(raw) {
  if (!raw) return false;
  const v = String(raw).trim();
  if (v.length > 254) return false;
  if (!EMAIL_RE.test(v)) return false;
  // Reject obvious junk like ".commmmmm" — final label longer than typical TLD.
  const finalLabel = v.split(".").pop();
  if (finalLabel.length > 12) return false;
  return true;
}

import { useLanguage } from "@/context/LanguageContext";
import { isValidPhoneNumber } from "libphonenumber-js";

const ERRORS = {
  tr: {
    phoneRequired: "Telefon numarası zorunludur.",
    phoneInvalid: "Geçerli bir telefon numarası girin (örn. 0532 123 45 67).",
    emailRequired: "E-posta zorunludur.",
    emailInvalid: "Geçerli bir e-posta adresi girin (örn. ad@domain.com).",
  },  en: {
    phoneRequired: "Phone number is required.",
    phoneInvalid: "Please enter a valid phone number with country code.",
    emailRequired: "Email is required.",
    emailInvalid: "Please enter a valid email (e.g. name@domain.com).",
  },
};

/**
 * Locale-aware phone validation.
 * Her iki dilde de ülke kodlu (E.164) numaralar libphonenumber ile doğrulanır;
 * TR'de eski alışkanlık olan "05XX..." yazımı da kabul edilir.
 */
export function getPhoneError(raw, { required = true } = {}, locale = "tr") {
  const dict = ERRORS[locale] || ERRORS.tr;
  if (!raw || !String(raw).trim()) {
    return required ? dict.phoneRequired : "";
  }
  const value = String(raw).trim();
  const ok = value.startsWith("+")
    ? isValidPhoneNumber(value)
    : locale === "en"
      ? isValidPhoneNumber(value)
      : isValidTurkishPhone(value);
  return ok ? "" : dict.phoneInvalid;
}

export function getEmailError(raw, { required = false } = {}, locale = "tr") {
  const dict = ERRORS[locale] || ERRORS.tr;
  if (!raw || !String(raw).trim()) {
    return required ? dict.emailRequired : "";
  }
  if (!isValidEmail(raw)) {
    return dict.emailInvalid;
  }
  return "";
}

/**
 * Convenience React hook — returns locale-aware validator functions.
 * Usage: const { getPhoneError, getEmailError } = useValidators();
 */
export function useValidators() {
  const { locale } = useLanguage();
  return {
    getPhoneError: (raw, opts) => getPhoneError(raw, opts, locale),
    getEmailError: (raw, opts) => getEmailError(raw, opts, locale),
  };
}
